import { forwardRef, useContext, useImperativeHandle, useRef } from "react";
import styled from "styled-components";
import { Button } from "../Button";
import { Context } from "../../context/WalletContext";
import MetamaskLogo from "../../assets/svg/MetamaskLogo";
import ImmutableLogo from "../../assets/svg/ImmutableLogo";
import WalletManagerModal from "./WalletManagerDialog";

const StyledSellWalletWarningModal = styled.dialog`
    width: 600px;
    height: 350px;
    background: #1C1C1C;
    border-radius: 32px;
    display: none;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;     
    padding: 24px;
    border-width: 0px;

    &::backdrop {        
        background-color: #00000090;
    }
    
    &[open]{
        display: flex;
    }
    
    #title{
        font-family: Exo 2;
        font-weight: 400;
        font-size: 30px;
        line-height: 100%;
        letter-spacing: 0%;
        color: #FFFFFF;
        text-align: center;

    }
       
    .subtitle{
        font-family: Exo 2;
        font-weight: 300;
        font-size: 16px;
        line-height: 100%;
        letter-spacing: 0%;
        color: #A6A6A6;
        text-align: center;
    }

    #action-container {
        display: flex;
        justify-content: space-evenly;
        align-items: center;
        width: 100%;
    }

    #minting-wallet-container {
        height: 60px;
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 20px;     

        .wallet-icon{
            margin-right: 20px;
        }

        .spacer{
            height: 60px;
            display: flex;
            flex: 2;
        }
    
        .wallet-info{
            display: flex;
            flex-direction: column;        
            justify-content: space-between;
            align-items: start;        
            margin-right: 20px;
        }
    
        .wallet-address{
            color: #FFFFFF;
        }
    
        .wallet-provider-connection{
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
    
        .wallet-provider-name{
            margin: 0px 10px 0px 0px;
            color: #38EF7D;
            font-family: Exo 2;
            font-weight: 300;
            font-size: 18px;
            line-height: 100%;
            letter-spacing: 0%;                
        }
    }

    #change-minting-wallet{
        width: 100px;
        height: 30px;
        border-color: #38EF7D;
        border-radius: 16px;
        border-width: 0.4px;
        border-style: solid;
        display: flex;
        justify-content: center;
        align-items: center; 
        color: #FFFFFF;
        cursor: pointer;
    }
    `;

const  SellWalletWarningModal = forwardRef(function SellWalletWarningModal({ onConfirm, subtitle, secondarySubtitle, confirmButtonText, warnedWalletKind, walletAddress }, ref) {

    const modalRef = useRef();
    const changeWalletModalRef = useRef();
    const walletContext = useContext(Context);

    useImperativeHandle(ref, () => {
        return {
            open: open,
            close: close,
        };
    });

    function open() {
        modalRef.current.showModal();
    }

    function close() {
        modalRef.current.close();
    }

    function isMetamaskWallet() {
        return walletContext?.state?.userWallet?.wallets?.metamask?.includes(walletAddress);
    }

    function shortenAddress(address) {
        if (address) {

            if (address.length <= 22) return address;
            return `${address.slice(0, 18)}...${address.slice(-4)}`;
        } else {
            return 'No Wallet Address Found'
        }
    }

    function onChangeWallet() {
        changeWalletModalRef.current.open();
    }

    return (<StyledSellWalletWarningModal ref={modalRef}>
        <WalletManagerModal ref={changeWalletModalRef}></WalletManagerModal>
        <p id="title">
            Confirmation Required
        </p>
        <p className="subtitle">
            {subtitle}
        </p>
        <div id="minting-wallet-container">
            <div className="wallet-icon">
                {
                    isMetamaskWallet() ?
                        <MetamaskLogo width={60} height={60} />
                        :
                        <ImmutableLogo width={60} height={60}></ImmutableLogo>
                }
            </div>
            <div className="wallet-info">
                <div onClick={function () {
                    return navigator.clipboard.writeText(walletAddress);
                }}>
                    <p className="wallet-address">{shortenAddress(walletAddress)}
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 15C9 12.1716 9 10.7574 9.87868 9.87868C10.7574 9 12.1716 9 15 9H16C18.8284 9 20.2426 9 21.1213 9.87868C22 10.7574 22 12.1716 22 15V16C22 18.8284 22 20.2426 21.1213 21.1213C20.2426 22 18.8284 22 16 22H15C12.1716 22 10.7574 22 9.87868 21.1213C9 20.2426 9 18.8284 9 16V15Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M16.9999 9C16.9975 6.04291 16.9528 4.51121 16.092 3.46243C15.9258 3.25989 15.7401 3.07418 15.5376 2.90796C14.4312 2 12.7875 2 9.5 2C6.21252 2 4.56878 2 3.46243 2.90796C3.25989 3.07417 3.07418 3.25989 2.90796 3.46243C2 4.56878 2 6.21252 2 9.5C2 12.7875 2 14.4312 2.90796 15.5376C3.07417 15.7401 3.25989 15.9258 3.46243 16.092C4.51121 16.9528 6.04291 16.9975 9 16.9999" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </p>
                </div>
                <div className="wallet-provider-connection">
                    <p className="wallet-provider-name">{warnedWalletKind}</p>
                </div>
            </div>
            <div className="spacer"></div>
            <div id="change-minting-wallet" onClick={onChangeWallet}>Change</div>
        </div>
        <p className="subtitle">            
            {secondarySubtitle}
        </p>
        <div id="action-container">
            <Button
                label={confirmButtonText}
                onClick={() => {
                    onConfirm();
                }}
            />
            <span style={{ width: 10, display: "inline-block" }} />
            <Button
                transparent
                label="Cancel"
                onClick={() => close()}
            />
        </div>
    </StyledSellWalletWarningModal>);
});

export default SellWalletWarningModal;